/* AvailableSerialsForItem
 * Shows real available serials for an item at a given clinic/warehouse — useful
 * for historical bills/SOs that Zoho synced without per-line-item serial linkage.
 * Click → expands a popover listing serial numbers (each a SerialHistoryLink).
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { SerialHistoryLink } from './SerialHistoryLink';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Barcode, CaretDown } from '@phosphor-icons/react';

export function AvailableSerialsForItem({ itemId, clinicId, warehouseName, qty, status = 'available' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!opened || data || !itemId) return;
    setLoading(true);
    const params = new URLSearchParams({ item_id: itemId, status });
    if (clinicId) params.set('clinic_id', clinicId);
    else if (warehouseName) params.set('warehouse_name', warehouseName);
    api.get(`/serials/by-item?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(() => setData({ serials: [], total_matching: 0 }))
      .finally(() => setLoading(false));
  }, [opened, itemId, clinicId, warehouseName, status, data]);

  if (!itemId) return <span className="text-[#94A3B8]">-</span>;

  return (
    <Popover open={opened} onOpenChange={setOpened}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[11px] text-[#0F4C81] hover:underline cursor-pointer"
          data-testid={`available-serials-${itemId}`}
        >
          <Barcode size={12} />
          <span>view available</span>
          <CaretDown size={10} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="text-xs font-semibold text-[#0F4C81] mb-2">
          Available serials for this item
        </div>
        {loading ? (
          <div className="text-[11px] text-[#64748B] py-2">Loading…</div>
        ) : data ? (
          <>
            <div className="text-[10px] text-[#64748B] mb-2">
              {data.total_matching} in stock{warehouseName ? ` at ${warehouseName}` : (clinicId ? ' at this branch' : '')}
              {qty ? ` · billed qty ${qty}` : ''}
            </div>
            {data.serials.length === 0 ? (
              <div className="text-[11px] text-[#94A3B8] italic">
                No serials currently in stock for this item{warehouseName ? ` at ${warehouseName}` : ''}.
                Zoho did not record serial-to-bill linkage for historical purchases.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1 max-h-64 overflow-y-auto">
                {data.serials.map(s => (
                  <SerialHistoryLink
                    key={s.serial_number}
                    serialNumber={s.serial_number}
                    className="bg-[#E8F5E9] text-[#0F4C81]"
                  />
                ))}
              </div>
            )}
            {data.total_matching > data.serials.length && (
              <div className="text-[10px] text-[#64748B] mt-2 italic">
                Showing first {data.serials.length} of {data.total_matching}
              </div>
            )}
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
